import { memo, useMemo } from 'react';

import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import MonthTransaction from './components/MonthTransaction';

function ListTransaction({ monthTransactions, isLoading, hasMore, handleLoadMore }) {
  const monthTransactionList = useMemo(
    () =>
      monthTransactions.map(({ month, year, transactions }) => (
        <MonthTransaction
          key={month}
          month={month}
          transactions={transactions}
          year={year}
        />
      )),
    [monthTransactions]
  );

  return (
    <Stack
      aria-label="List of transactions"
      direction="column"
      role="list"
    >
      <Stack
        direction="column"
        spacing="16px"
      >
        {monthTransactionList}
      </Stack>
      {hasMore && (
        <Stack
          direction="row"
          justifyContent="center"
          pt="40px"
          spacing="8px"
        >
          <LoadingButton
            loading={isLoading}
            onClick={handleLoadMore}
            variant="outlined"
          >
            Load more
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}

ListTransaction.propTypes = {
  monthTransactions: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string.isRequired,
      year: PropTypes.string.isRequired,
      transactions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
          direction: PropTypes.string.isRequired,
          createdAt: PropTypes.string.isRequired,
          summary: PropTypes.string.isRequired,
          amount: PropTypes.shape({
            amount: PropTypes.number.isRequired,
            currency: PropTypes.string.isRequired,
            formattedAmount: PropTypes.string.isRequired
          }),
          balance: PropTypes.shape({
            amount: PropTypes.number.isRequired,
            currency: PropTypes.string.isRequired,
            formattedAmount: PropTypes.string.isRequired
          }),
          fromAccount: PropTypes.string,
          description: PropTypes.string,
          toAccount: PropTypes.string,
          traceNumber: PropTypes.string,
          reason: PropTypes.string,
          merchant: PropTypes.string,
          atm: PropTypes.string,
          cardNetwork: PropTypes.string,
          relatedParty: PropTypes.string,
          checkDepositId: PropTypes.string,
          recipientAccountNumber: PropTypes.string,
          recipientName: PropTypes.string,
          senderAccountNumber: PropTypes.string,
          senderName: PropTypes.string,
          counterpartyId: PropTypes.string,
          // From
          from: PropTypes.shape({
            accountHolder: PropTypes.string,
            routingNumber: PropTypes.string,
            fullAccountNumber: PropTypes.string
          }),
          // To
          to: PropTypes.shape({
            accountHolder: PropTypes.string,
            routingNumber: PropTypes.string,
            fullAccountNumber: PropTypes.string
          }),
          // Merchant Detail
          paymentMethod: PropTypes.string,
          digitalWallet: PropTypes.string,
          cardPresent: PropTypes.bool,
          recurring: PropTypes.bool,
          verificationMethod: PropTypes.string,
          merchantName: PropTypes.string,
          merchantLocation: PropTypes.string,
          merchantCategory: PropTypes.string,
          // ATM Details
          atmName: PropTypes.string,
          atmLocation: PropTypes.string,
          surcharge: PropTypes.shape({
            amount: PropTypes.number.isRequired,
            currency: PropTypes.string.isRequired,
            formattedAmount: PropTypes.string.isRequired
          }),
          // FX Details
          currencyConversion: PropTypes.shape({
            originalCurrency: PropTypes.string,
            amountInOriginalCurrency: PropTypes.number,
            fxRate: PropTypes.string
          })
        })
      ).isRequired
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  handleLoadMore: PropTypes.func
};

function areEqual(preProp, nextProp) {
  return (
    preProp.isLoading === nextProp.isLoading &&
    preProp.monthTransactions === nextProp.monthTransactions
  );
}

export default memo(ListTransaction, areEqual);
